import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye, Download, CheckCircle2, XCircle, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { money, fmtDate, fmtDateTime } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const KIND = { id: "Photo ID", paystub: "Pay stub / income", other: "Other" };

export async function openDocument(doc, download = false) {
  const { data } = await api.get(`/application-files/${doc.id}`, { params: { inline: !download }, responseType: "blob" });
  const url = URL.createObjectURL(new Blob([data], { type: doc.content_type }));
  if (download || doc.content_type === "image/heic") {
    const a = document.createElement("a");
    a.href = url; a.download = doc.filename; document.body.appendChild(a); a.click(); a.remove();
  } else {
    window.open(url, "_blank", "noopener");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-4 py-2 text-sm"><dt className="text-muted-foreground">{k}</dt><dd className="num text-right font-medium" data-testid={testid}>{v || "—"}</dd></div>
);

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonErr, setReasonErr] = useState("");
  const load = useCallback(() => api.get(`/applications/${id}`).then((r) => setA(r.data)).catch(() => setA(false)), [id]);
  useEffect(() => { load(); }, [load]);

  const review = async () => {
    try { setA((await api.post(`/applications/${id}/review`)).data); toast.success("Marked under review"); } catch (e) { toast.error(apiError(e).message); }
  };
  const reject = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setReasonErr("A reason is required to reject an application"); return; }
    try {
      setA((await api.post(`/applications/${id}/reject`, { reason })).data);
      setRejecting(false);
      toast.success("Application rejected");
    } catch (err) { const { message, fields } = apiError(err); setReasonErr(fields.reason || message); }
  };
  const view = async (d, dl) => { try { await openDocument(d, dl); } catch (e) { toast.error(apiError(e).message); } };

  if (a === false) return <div className="py-20 text-center text-muted-foreground" data-testid="application-not-found">Application not found</div>;
  if (!a) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;
  const open = ["submitted", "under_review"].includes(a.status);

  return (
    <div data-testid="application-detail">
      <Link to="/admin/applications" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All applications</Link>
      <PageHeader eyebrow={`Submitted ${fmtDate(a.submitted_at)}`}
        title={<span className="flex flex-wrap items-center gap-3">{a.applicant_name} <StatusBadge status={a.status} testid="application-status-badge" /></span>}
        subtitle={<>{a.applicant_email} · {money(a.amount_cents)} · {a.term_months} months{a.loan_number && <> · Loan <Link className="font-semibold text-primary hover:underline" to={`/admin/loans/${a.loan_id}`} data-testid="application-loan-link">{a.loan_number}</Link></>}</>}
        actions={open && <>
          {a.status === "submitted" && <Button variant="outline" onClick={review} data-testid="mark-under-review-button"><ClipboardCheck /> Mark under review</Button>}
          <Button variant="outline" className="text-destructive hover:text-destructive" onClick={() => { setRejecting(true); setReason(""); setReasonErr(""); }} data-testid="reject-application-button"><XCircle /> Reject</Button>
          <Button onClick={() => navigate(`/admin/loans/new?application=${a.id}`)} data-testid="approve-application-button"><CheckCircle2 /> Approve</Button>
        </>} />
      {a.status === "rejected" && <div className="mb-6 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm" data-testid="application-reject-reason">Rejected: {a.reject_reason}</div>}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="fade-up rounded-lg border bg-card p-6">
          <h3 className="text-lg font-semibold">Basics</h3>
          <dl className="mt-3 divide-y">
            <Row k="Phone" v={a.phone} /><Row k="Address" v={a.address} /><Row k="Amount requested" v={money(a.amount_cents)} testid="app-amount" />
            <Row k="Purpose" v={a.purpose} /><Row k="Requested term" v={`${a.term_months} months`} testid="app-term" />
          </dl>
        </div>
        <div className="fade-up rounded-lg border bg-card p-6" style={{ animationDelay: "60ms" }}>
          <h3 className="text-lg font-semibold">Income & employment</h3>
          <dl className="mt-3 divide-y">
            <Row k="Status" v={a.employment_status} /><Row k="Employer" v={a.employer} /><Row k="Job title" v={a.job_title} />
            <Row k="Years at employer" v={a.years_at_employer} /><Row k="Gross monthly income" v={money(a.gross_monthly_income_cents)} />
            <Row k="Other monthly income" v={money(a.other_monthly_income_cents)} /><Row k="Monthly housing payment" v={money(a.housing_payment_cents)} />
          </dl>
          <div className="mt-4 rounded-md bg-muted/50 p-4">
            <div className="eyebrow">Debt-to-income (housing)</div>
            <div className="num mt-1 font-display text-3xl font-semibold" data-testid="app-dti">{a.dti_percent == null ? "—" : `${a.dti_percent.toFixed(1)}%`}</div>
            <div className="text-xs text-muted-foreground">Housing payment ÷ (gross + other income)</div>
          </div>
        </div>
        <div className="fade-up rounded-lg border bg-card p-6" style={{ animationDelay: "120ms" }}>
          <h3 className="text-lg font-semibold">Documents ({a.documents.length})</h3>
          <ul className="mt-3 divide-y">
            {a.documents.map((d) => (
              <li key={d.id} className="py-3" data-testid={`app-doc-${d.id}`}>
                <div className="text-sm font-medium">{d.filename}</div>
                <div className="text-xs text-muted-foreground">{KIND[d.kind]} · {(d.size / 1024).toFixed(0)} KB</div>
                <div className="mt-1 flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => view(d, false)} data-testid={`view-doc-${d.id}`}><Eye /> View</Button>
                  <Button variant="ghost" size="sm" onClick={() => view(d, true)} data-testid={`download-doc-${d.id}`}><Download /> Download</Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="fade-up mt-6 rounded-lg border bg-card" data-testid="application-activity">
        <h3 className="border-b px-6 py-4 text-lg font-semibold">Activity</h3>
        <ul className="divide-y">
          {[...a.activity].reverse().map((x, i) => (
            <li key={i} className="flex flex-wrap items-baseline justify-between gap-2 px-6 py-3 text-sm" data-testid={`application-activity-${i}`}>
              <span className="flex items-center gap-2">{x.status && <StatusBadge status={x.status} />}{x.details}</span>
              <span className="num text-xs text-muted-foreground">{fmtDateTime(x.at)} ET · {x.actor}</span>
            </li>
          ))}
        </ul>
      </div>
      <AlertDialog open={rejecting} onOpenChange={setRejecting}>
        <AlertDialogContent data-testid="reject-dialog">
          <form onSubmit={reject} className="space-y-4">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display text-2xl">Reject application</AlertDialogTitle>
              <AlertDialogDescription>The applicant will see this reason and receive it by email.</AlertDialogDescription>
            </AlertDialogHeader>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="reject-reason-input" />
            {reasonErr && <p className="text-xs font-medium text-destructive" data-testid="error-reject-reason">{reasonErr}</p>}
            <AlertDialogFooter>
              <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
              <Button type="submit" variant="destructive" data-testid="reject-confirm-button">Reject application</Button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
